import React, { useEffect, useState } from "react";
import {
  FaUser,
  FaEnvelope,
  FaPhone,
  FaGlobe,
  FaComment,
  FaPaperPlane,
  FaMapMarkerAlt,
  FaCheckCircle,
  FaBuilding,
  FaHeadset,
  FaChevronRight,
  FaRegClock,
  FaShieldAlt,
} from "react-icons/fa";
import {
  FiMail,
  FiPhone,
  FiMapPin,
  FiSend,
  FiChevronDown,
} from "react-icons/fi";
import { MdOutlineSupportAgent } from "react-icons/md";
import { HiOutlineSparkles } from "react-icons/hi";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { useDispatch, useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import {
  submitContactUs,
  resetContactUsState,
} from "../../src/Redux/ReduxSlices/contactUsSlice";
import { toast } from "sonner";
import { countryData } from "../utils/CountryCode";
import { Phone } from "lucide-react";
import Meta from "../components/Meta/Meta";

const ContactUs = () => {
  const dispatch = useDispatch();
  const { loading, success, error } = useSelector(
    (state) => state.contactUs || {},
  );

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    mobile: "",
    country: "",
    message: "",
  });

  const [activeField, setActiveField] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      firstname: formData.firstName.trim(),
      lastname: formData.lastName.trim(),
      email: formData.email.trim(),
      phone_number: formData.mobile.trim(),
      message: formData.message.trim(),
    };

    if (!payload.firstname || !payload.email || !payload.message) {
      toast.error(
        "Please fill in required fields: first name, email and message.",
      );
      return;
    }

    dispatch(submitContactUs(payload));
  };

  useEffect(() => {
    if (success) {
      toast.success(
        "Thank you for your message! We will get back to you soon.",
      );
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        mobile: "",
        country: "",
        message: "",
      });
      setTimeout(() => dispatch(resetContactUsState()), 3000);
    }
    if (error) {
      toast.error(`Error: ${error}`);
      dispatch(resetContactUsState());
    }
  }, [success, error, dispatch]);

  const contactInfo = [
    {
      icon: FiMapPin,
      title: "Visit Our Studio",
      content: "Digikore Studios Limited",
      address: "Lalwani Triumph, Viman Nagar, Pune, MH 411014",
      color: "text-rose-500",
      bg: "bg-rose-50",
      delay: 0.1,
    },
    {
      icon: FiMail,
      title: "Email Support",
      content: "support@maxipitch.com",
      response: "Typically replies within 4 hours",
      color: "text-indigo-500",
      bg: "bg-indigo-50",
      delay: 0.2,
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="min-h-screen flex flex-col bg-white"
    >
      <Meta
        title="Contact MaxiPitch | Get Support & Sales Information"
        description="Contact MaxiPitch for support, product details, or sales inquiries. Our team is ready to help you automate lead generation and grow your business."
        url="https://maxipitch.com/contact-us"
      />
      <Header />
      <motion.div
        initial={{ y: 40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="py-8 bg-gradient-to-r from-indigo-500/5 via-purple-500/5 to-pink-500/5 text-center pt-14 max-md:pt-8 max-md:px-4"
      >
        <h1 className=" bg-gradient-to-r from-gray-900 via-indigo-800 to-purple-800 bg-clip-text text-transparent  max-md:mb-2 ">
          Get in Touch with MaxiPitch
        </h1>

        <p className="text-xl max-md:text-base text-gray-500 max-w-2xl mx-auto max-md:px-2 ">
          Whether you have questions, need support, or want to discuss a
          project, we're here to help you succeed.
        </p>
      </motion.div>
      {/* Main Content */}
      <main className="flex-grow">
        {/* Hero Section */}
        <div className="relative overflow-hidden bg-gradient-to-br from-gray-50 via-white to-gray-50 ">
          {/* Geometric Background */}
          <div className="absolute inset-0">
            <div className="absolute top-0 left-1/4 w-72 h-72 bg-gradient-to-r from-blue-100 to-transparent rounded-full mix-blend-multiply filter blur-3xl opacity-30"></div>
            <div className="absolute bottom-0 right-1/4 w-72 h-72 bg-gradient-to-l from-purple-100 to-transparent rounded-full mix-blend-multiply filter blur-3xl opacity-30"></div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="max-w-7xl mx-auto  px-4 mt-6 pb-12">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Left Column - Contact Info */}
            <motion.div
              initial={{ x: -30, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="lg:col-span-1 space-y-8"
            >
              {/* Contact Cards */}
              <div className="space-y-6">
                {contactInfo.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ x: -30, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: item.delay }}
                    whileHover={{ y: -5, scale: 1.02 }}
                    className="group cursor-pointer"
                  >
                    <div className="bg-white p-6 rounded-2xl shadow-sm border border-indigo-200 hover:shadow-lg  transition-all duration-300">
                      <div className="flex items-start gap-4">
                        <div className={`${item.bg} p-3 rounded-lg font-bold`}>
                          <item.icon className={`text-xl  ${item.color}`} />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-bold text-gray-900 text-lg mb-2">
                            {item.title}
                          </h3>
                          <p className="text-gray-800 font-semibold mb-1">
                            {item.content}
                          </p>
                          <p className="text-gray-500 text-sm">
                            {item.address || item.response || item.hours}
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Support Hours */}

              {/* Quick Links */}
            </motion.div>

            {/* Right Column - Contact Form */}
            <motion.div
              initial={{ x: 30, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="lg:col-span-2"
            >
              <div className="bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden mb-4 p-6 max-md:p-2">
                {/* Form Header */}
                <div className="p-3 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 mb-2">
                        Send Your Message
                      </h2>
                      <p className="text-gray-600">
                        Fill out the form below and we'll respond as soon as
                        possible
                      </p>
                    </div>
                    <div className="hidden md:block">
                      <motion.div
                        animate={{ rotate: [0, 360] }}
                        transition={{
                          duration: 20,
                          repeat: Infinity,
                          ease: "linear",
                        }}
                        className="p-3 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"
                      >
                        <FiSend className="text-2xl text-white" />
                      </motion.div>
                    </div>
                  </div>
                </div>

                {/* Form */}
                <div className="p-4 ">
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Name Fields */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-1">
                        <label className="block text-sm font-medium text-gray-700">
                          First Name <span className="text-rose-500">*</span>
                        </label>
                        <div className="relative">
                          <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                            <FaUser className="text-gray-400" />
                          </div>
                          <input
                            type="text"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            required
                            placeholder="John"
                            className="w-full pl-12 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-all hover:border-gray-300"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Last Name
                        </label>
                        <div className="relative">
                          <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                            <FaUser className="text-gray-400" />
                          </div>
                          <input
                            type="text"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            placeholder="Doe"
                            className="w-full pl-12 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-all hover:border-gray-300"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Email Field */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Email Address <span className="text-rose-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                          <FaEnvelope className="text-gray-400" />
                        </div>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="you@company.com"
                          className="w-full pl-12 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-all hover:border-gray-300"
                        />
                      </div>
                    </div>

                    {/* Phone & Country */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Phone Number
                        </label>
                        <div className="relative">
                          <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                            <FaPhone className="text-gray-400 scale-x-[-1]" />
                          </div>
                          <input
                            type="tel"
                            name="mobile"
                            value={formData.mobile}
                            onChange={handleChange}
                            placeholder="+1 (555) 123-4567"
                            className="w-full pl-12 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-all hover:border-gray-300"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          Country
                        </label>
                        <div className="relative">
                          <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                            <FaGlobe className="text-gray-400" />
                          </div>
                          <select
                            name="country"
                            value={formData.country}
                            onChange={handleChange}
                            className="w-full pl-12 pr-12 py-2 bg-gray-50 border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-all hover:border-gray-300 appearance-none cursor-pointer"
                          >
                            <option value="">Select your country</option>
                            {countryData.map((country, index) => (
                              <option
                                key={`${country.code}-${index}`}
                                value={country.name}
                              >
                                {country.name}
                              </option>
                            ))}
                          </select>
                          <FiChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        </div>
                      </div>
                    </div>

                    {/* Message Field */}
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Your Message <span className="text-rose-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute left-4 top-4">
                          <FaComment className="text-gray-400" />
                        </div>
                        <textarea
                          name="message"
                          rows={3}
                          value={formData.message}
                          onChange={handleChange}
                          required
                          placeholder="Tell us about your inquiry, project, or question..."
                          className="w-full pl-12 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none transition-all hover:border-gray-300 resize-none"
                        />
                      </div>
                    </div>

                    {/* Privacy & Submit */}
                    <div className="pt-3">
                      <div className="flex items-center mb-6 justify-center max-md:justify-start">
                        <input
                          type="checkbox"
                          id="privacy"
                          required
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <label
                          htmlFor="privacy"
                          className="ml-2 text-sm max-md:text-xs text-gray-600"
                        >
                          I agree to the{" "}
                          <a
                            href="/privacy-policy"
                            className="text-blue-600 hover:underline"
                          >
                            Privacy Policy
                          </a>{" "}
                          and consent to being contacted
                        </label>
                      </div>

                      <div className="flex justify-center items-center">
                        <motion.button
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          type="submit"
                          disabled={loading}
                          className="w-1/2 max-md:w-full    main-button text-white py-2 max-md:py-1.5 rounded-md font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-3 disabled:opacity-70 disabled:cursor-not-allowed group"
                        >
                          {loading ? (
                            <>
                              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                              Sending Your Message...
                            </>
                          ) : (
                            <>
                              Send Message
                              <motion.div
                                animate={{ x: [0, 5, 0] }}
                                transition={{ repeat: Infinity, duration: 1.5 }}
                              >
                                <FaPaperPlane className="text-lg" />
                              </motion.div>
                            </>
                          )}
                        </motion.button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>

              {/* Additional Info */}
            </motion.div>
          </div>
        </div>

        {/* CTA Section */}
      </main>

      {/* Success Modal */}
      <AnimatePresence>
        {success && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-3xl p-8 max-w-md w-full shadow-2xl border border-gray-100"
            >
              <div className="text-center">
                <div className="relative inline-block mb-6">
                  <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-green-500 rounded-full blur-xl opacity-60"></div>
                  <div className="relative w-20 h-20 bg-gradient-to-r from-emerald-500 to-green-600 rounded-full flex items-center justify-center shadow-lg">
                    <FaCheckCircle className="text-3xl text-white" />
                  </div>
                </div>

                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  Message Sent Successfully!
                </h3>

                <p className="text-gray-600 mb-2">
                  Thank you for contacting MaxiPitch.
                </p>
                <p className="text-gray-500 text-sm mb-6">
                  Our team will reach out to you within 4 hours.
                </p>

                <div className="space-y-3">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => dispatch(resetContactUsState())}
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 rounded-md font-semibold"
                  >
                    Close
                  </motion.button>
                  <button
                    onClick={() => (window.location.href = "/dashboard")}
                    className="w-full py-3 text-gray-600 hover:text-gray-900 font-medium transition-colors"
                  >
                    Go to Dashboard
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <Footer />
    </motion.div>
  );
};

export default ContactUs;
